import React from 'react';
import * as Elevated from '@pitaman71/omniglot-introspect';
import timezones from 'timezones-list';

import { makeStyles, FormControl, FormGroup, Grid, InputLabel, MenuItem, Select, TextField } from '@material-ui/core';
import { TextFieldProps } from '@material-ui/core/TextField';
import { Autocomplete } from '@material-ui/lab';

import { Annotations, Clients, Streams } from '@pitaman71/omniglot-live-data';
import { Domains } from '@pitaman71/omniglot-live-domains';
import { Propose } from '..';
import { AsTextField } from '.';

const __moduleName__ = `onniglot-live-react/MaterialUi`;

export type DateFormats = 'YMD'|'DMY'|'MDY';

const useStyles = makeStyles((theme) => ({
    formControl: {
      margin: theme.spacing(1),
      wrap: 'nowrap'
    },
}));

export function Time(props: Annotations.Annotation<JSX.Element> & {
    value?: Domains.Temporal._Time,
    client?: Clients.ScalarClient<Domains.Temporal._Time>,
    inputProps?: TextFieldProps
}): JSX.Element {
    const elementRef = React.useRef<HTMLDivElement>(null);

    const classes = useStyles();

    const domain = Domains.Temporal.TimeDomain;

    return (
        <React.Fragment>
        <InputLabel shrink style={{ display: 'flex' }}>{props.label}</InputLabel>
        <FormControl className={classes.formControl}>
            <TimeFields
                hour={props.value?.hour}
                setHour={(value_: number|undefined) => {
                    if(value_ === undefined) {
                        // skip
                    } else {
                        props.client?.assign({ 
                            ...props.value, 
                            hour: value_ 
                        });
                    }
                }}
                minute={props.value?.minute}
                setMinute={(value_: number|undefined) => {
                    if(domain === undefined) {
                        // skip
                    } else {
                        props.client?.assign({ 
                            ...props.value,
                            minute: value_ 
                        });
                    }
                }}
                zone={props.value?.zone?.name}
                setZone={(value_: string|undefined) => {
                    if(domain === undefined) {
                        // skip
                    } else if(value_ === undefined) {
                        const tzName = Intl.DateTimeFormat().resolvedOptions().timeZoneName;
                        if(tzName !== undefined)
                            props.client?.assign({ zone: Domains.Temporal.ZoneDomain.getByName(tzName) });
                    } else {
                        props.client?.assign({ ...props.value, zone: Domains.Temporal.ZoneDomain.getByName(value_) });
                    }
                }}
            />
        </FormControl>
        </React.Fragment>
    );
}

export function Date(props: Partial<Annotations.Annotation<JSX.Element>> & {
    value?: Partial<Domains.Temporal._Date>,
    client?: Clients.ScalarClient<Partial<Domains.Temporal._Date>>,
    inputProps?: TextFieldProps
}): JSX.Element {
    const classes = useStyles();

    return (
        <React.Fragment>
            <InputLabel shrink style={{ display: 'flex' }}>{props.label}</InputLabel>
            <FormControl className={classes.formControl} fullWidth={true}>
                    <AsTextField.Date {... props} inputProps={{ fullWidth: true }}></AsTextField.Date>
            </FormControl>
        </React.Fragment>
    );
}

const monthNames = [
    'January', 'February', 'March', 'April',
    'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December'
];

export function TimeFields(props: {
    hour: number|undefined, setHour: (hour_: number|undefined) => void,
    minute: number|undefined, setMinute: (minute_: number|undefined) => void,
    zone: string|undefined, setZone: (zone_: string|undefined) => void
}) {
        return (
            <Grid container>
                { /* hour */ }
                <Grid item xs={4}>
                    <TextField type="text" placeholder='hh' 
                        value={props.hour} 
                        onKeyPress={(e:any) => {
                            if(!/^\d$/.test(e.key)) {
                                e.preventDefault();
                            }
                            console.log(e)
                        }}
                        onChange={(e:any) => props.setHour(e.target.value)}
                    />
                </Grid>
                <Grid item xs={1}>
                    :
                </Grid>
                { /* minute */ }
                <Grid item xs={4}>
                    <TextField type="text" placeholder='mm' 
                        value={props.minute} 
                        onKeyPress={(e:any) => {
                            if(!/^\d$/.test(e.key)) {
                                e.preventDefault();
                            }
                            console.log(e)
                        }}
                        onChange={(e:any) => props.setMinute(e.target.value)}
                    />
                </Grid>
                <Grid item xs={1}>
                </Grid>
                { /* zone */ }
                <Grid item xs={2}>
                    <Select 
                        value={props.zone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                        onChange={(e:any) => props.setZone(e.target.value)}
                    >
                        { timezones.map((timezone) => {
                            return (
                                <MenuItem value={timezone.name}>{timezone.label}</MenuItem>
                            );
                        })}
                    </Select>
                </Grid>
            </Grid>
        );
}

export function YMD_DateFields(props: {
    year: number|undefined, setYear: (year_: number|undefined) => void,
    month: number|undefined, setMonth: (month_: number|undefined) => void,
    day: number|undefined, setDay: (day_: number|undefined) => void
}) {
    const options = monthNames.map((name, index) => { return { name, index: index+1 } });
    const month = props.month === undefined ? null : options[props.month - 1];
    return (
        <FormGroup row style={{ display: "block" }}>
            { /* year */ }
            <TextField type="text" size='small'
                value={props.year} placeholder='yyyy'  
                onKeyPress={(e:any) => {
                    if(!/^\d$/.test(e.key)) {
                        e.preventDefault();
                    }
                    console.log(e)
                }}
                onChange={(e:any) => props.setYear(e.target.value)}
            />
            { /* month */ }
            <Autocomplete
                options={options}
                autoHighlight
                getOptionLabel={ (option) => option.name }
                value={month}
                onChange={(e:any, value_) => {
                    props.setMonth(value_?.index);
                }}
                renderInput={(params:any) => (
                    <TextField
                        { ...params }
                        placeholder='month'
                    />
                )}
            />
            { /* day */ }
            <TextField type="text" size='small' 
                value={props.day} placeholder='dd'
                onKeyPress={(e:any) => {
                    if(!/^\d$/.test(e.key)) {
                        e.preventDefault();
                    }
                    console.log(e)
                }}                
                onChange={(e:any) => props.setDay(e.target.value)}
            />
        </FormGroup>
    );
}
export function DateTime(props: Annotations.Annotation<JSX.Element> & {
    value?: Partial<Domains.Temporal._DateTime>,
    client?: Clients.ScalarClient<Partial<Domains.Temporal._DateTime>>,
    inputProps?: TextFieldProps
}): JSX.Element {
    const classes = useStyles();
    const domain = Domains.Temporal.DateTimeDomain;
    return (
        <FormControl className={classes.formControl}>
            <InputLabel>{props.label}</InputLabel>
            <Grid container>
                <Grid item xs={7}>
                    <YMD_DateFields
                        year={props.value?.date?.year}
                        setYear={(value_: number|undefined) => {
                            props.client?.assign({ ...props.value, date: { ...props.value, year: value_ } });
                        }}
                        month={props.value?.date?.month}
                        setMonth={(value_: number|undefined) => {
                            props.client?.assign({ ...props.value, date: { ...props.value?.date, month: value_ } });
                        }}
                        day={props.value?.date?.day}
                        setDay={(value_: number|undefined) => {
                            props.client?.assign({ ...props.value, date: { ...props.value?.date, day: value_ } });
                        }}
                    />
                </Grid>
                <Grid item xs={1}>
                    { /* spacing b/w date and time */ }
                </Grid>
                <Grid item xs={4}>
                    <TimeFields
                        hour={props.value?.time?.hour}
                        setHour={(value_: number|undefined) => {
                            props.client?.assign({ ...props.value, time: { ...props.value?.time, hour: value_ } });
                        }}
                        minute={props.value?.time?.minute}
                        setMinute={(value_: number|undefined) => {
                            props.client?.assign({ ...props.value, time: { ...props.value?.time, minute: value_ } });
                        }}
                        zone={props.value?.time?.zone?.name}
                        setZone={(value_: string|undefined) => {
                            const tzName = Intl.DateTimeFormat().resolvedOptions().timeZoneName;
                            props.client?.assign({ ...props.value, time: { ...props.value?.time, zone: !tzName ? undefined : Domains.Temporal.ZoneDomain.getByName(tzName) } });
                        }}
                            />
                </Grid>
            </Grid>
        </FormControl>
);
}
